import { useEffect, useState, useContext } from 'react';
import { Dialog, Backdrop, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, MenuItem, CircularProgress } from '@mui/material';
import { BlobClient, BlobServiceClient } from '@azure/storage-blob';
import { nanoid } from 'nanoid';
import { ModeContext } from './ModeContext';
import { encodeBase64UTF8 } from './Codec';
import { SendToCosmos, DeleteToCosmos } from './SendToCosmos';


const FileBUUploadModal = ({ open, onClose, fileData, onUpdated }) => {
  console.log('FileBUUploadModal', fileData);

  const { selectedFiles, setSelectedFiles, user } = useContext(ModeContext);
  const [isLoading, setIsLoading] = useState(false);
  const [filter1, setFilter1] = useState('');
  const [filter2, setFilter2] = useState('');
  const [filter3, setFilter3] = useState('');
  const [publicValue, setPublicValue] = useState('publish');

  // 公開/非公開の選択変更
  const handleChange = (event) => setPublicValue(event.target.value);

  // 初期値設定
  useEffect(() => {
    if (fileData) {
      setFilter1(fileData.filter1 || fileData.category || '');
      setFilter2(fileData.filter2 || '');
      setFilter3(fileData.filter3 || '');
      setPublicValue(fileData.status || fileData.public || 'publish');
    }
  }, [fileData]);


  // ファイル選択
  const handleFileChange = (event) => {
    setSelectedFiles(Array.from(event.target.files));
  };


  // 更新
  const handleUpdate = async () => {
    if (filter1 === '' || filter3 === '') {
      alert('未入力の箇所があります。');
      return;
    }

    const confirm = window.confirm('この保存しますか？');
    if (!confirm) return;
    const timestamp = Date.now();

    const containerSasUrl = `${process.env.REACT_APP_BLOB_URL}/${fileData.fileid}?comp=metadata&${process.env.REACT_APP_BLOB_TOKEN}`;

    try {
      const headers = {
        "x-ms-version": "2020-10-02",
        "x-ms-date": new Date().toUTCString(),
        "x-ms-meta-uploadedBy": encodeBase64UTF8(user),
        "x-ms-meta-user": encodeBase64UTF8(fileData.title),
        "x-ms-meta-filename": encodeBase64UTF8(fileData.filename),
        "x-ms-meta-bu": encodeBase64UTF8(fileData.bu),
        "x-ms-meta-filter1": encodeBase64UTF8(filter1),
        "x-ms-meta-filter2": encodeBase64UTF8(filter2),
        "x-ms-meta-filter3": filter3,
        "x-ms-meta-filter4": '',
        "x-ms-meta-filter5": 'u_doc',
        "x-ms-meta-status": publicValue,
        "x-ms-blob-type": "BlockBlob"
      };

      const res = await fetch(containerSasUrl, { method: "PUT", headers });

      if (res.ok) {
        // CosmosDBに1レコードとして保存
        const payload = {
          id: fileData.id,
          fileid: fileData.fileid,
          bu: fileData.bu,
          category: filter1,
          filename: fileData.filename,
          url: fileData.url,
          filter1: filter1,
          filter2: filter2,
          filter3: filter3,
          filter4: '',
          filter5: 'u_doc',
          status: publicValue,
          timestamp: timestamp,
          container: "con-concierge-filebu"
        };
        await SendToCosmos(payload);

        if (onUpdated) onUpdated();
        alert('保存しました。');
        onClose();
      } else {
        console.error("❌ エラーが発生:", res.statusText);
        alert('保存に失敗しました。');
      }
    } catch (error) {
      console.error('アップロードエラー:', error);
    }
  };


  // 新規
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || filter1 === '' || filter3 === '') {
      alert('未入力の箇所があります。');
      return;
    }

    setIsLoading(true);
    const timestamp = Date.now();
    const containerSasUrl = `${process.env.REACT_APP_BLOB_URL}?${process.env.REACT_APP_BLOB_TOKEN}`;

    try {
      const containerClient = new BlobServiceClient(containerSasUrl).getContainerClient('');

      const uploadPromises = selectedFiles.map(async (file) => {
        const uniqueId = nanoid();
        const blobName = `BU(User)/${fileData.bu}/${uniqueId}-${file.name}`;
        const blockBlobClient = containerClient.getBlockBlobClient(blobName);

        const metadata = {
          uploadedBy: user,
          user: localStorage.getItem('compId') || '',
          filename: encodeBase64UTF8(file.name),
          bu: encodeBase64UTF8(fileData.bu),
          filter1: encodeBase64UTF8(filter1),
          filter2: encodeBase64UTF8(filter2),
          filter3: filter3,
          filter4: '',
          filter5: 'u_doc',
          status: publicValue
        };

        const result = await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type },
          metadata: metadata,
        });

        if (result._response.status >= 200 && result._response.status < 300) {
          console.log("アップロード成功", file.name);

          // CosmosDBに1レコードとして保存
          const payload = {
            id: uniqueId,
            fileid: blobName,
            bu: fileData.bu,
            category: filter1,
            filename: file.name,
            url: blockBlobClient.url,
            filter1: filter1,
            filter2: filter2,
            filter3: filter3,
            filter4: '',
            filter5: 'u_doc',
            status: publicValue,
            timestamp: timestamp,
            container: "con-concierge-filebu"
          };
          await SendToCosmos(payload);

          if (onUpdated) onUpdated(); // 画面反映
        } else {
          console.warn("アップロードに問題があるかもしれません");
        }
      });

      await Promise.all(uploadPromises);
      setSelectedFiles([]);
      alert('保存完了しました。');
      document.activeElement.blur();
      onClose();
    } catch (error) {
      console.error('アップロードエラー:', error);
    } finally {
      setIsLoading(false);
    }
  };


  // 削除
  const handleDelete = async (fileData) => {
    const confirm = window.confirm('このファイルを削除しますか？');
    if (!confirm) return;
    setIsLoading(true);
    try {
      // Blob Storageから削除
      const containerSasUrl = `${process.env.REACT_APP_BLOB_URL}/${fileData.fileid}?${process.env.REACT_APP_BLOB_TOKEN}`;
      const blobClient = new BlobClient(containerSasUrl);
      await blobClient.delete();

      // CosmosDBから1レコード削除
      const payload = {
        id: fileData.id,
        container: "con-concierge-filebu"
      };
      await DeleteToCosmos(payload);

      if (onUpdated) onUpdated(); // 画面反映
      alert('ファイルを削除しました。');
      onClose();
    } catch (err) {
      alert('ファイルを削除に失敗しました。');
      console.error("削除エラー:", err.message);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      fullWidth
    >
      <DialogTitle>{fileData?.bu || "BU"}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mt: 2 }}>
          {fileData?.filename ? (
            <a 
              href={fileData.url} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: "#1976d2", textDecoration: "underline" }}
            >
              {fileData.filename}
            </a>
          ) : (
            ""
          )}
        </Typography>

        <TextField
          variant="outlined"
          fullWidth
          label="カテゴリー"
          margin="normal"
          value={filter1}
          onChange={(e) => setFilter1(e.target.value)}
          disabled={Boolean(fileData?.filename)} 
        />
        <TextField
          variant="outlined"
          fullWidth
          label="機種"
          margin="normal"
          value={filter2}
          onChange={(e) => setFilter2(e.target.value)}
        />

        <TextField
          variant="outlined"
          fullWidth
          label="グループ(半角英数)"
          margin="normal"
          value={filter3}
            onChange={(e) => {
            const value = e.target.value;
            if (/^[0-9A-Za-z]*$/.test(value)) {
              setFilter3(value);
            }}}
        />

        <TextField
          variant="outlined"
          fullWidth
          label="公開設定"
          margin="normal"
          value={publicValue}
          onChange={handleChange}
          select
        >
          <MenuItem value="publish">公開</MenuItem>
          <MenuItem value="private">非公開</MenuItem>
        </TextField>

        {!fileData?.filename && (
          <>
            <input
              type="file"
              multiple 
              onChange={handleFileChange}
            />

            <ul>
              {selectedFiles.map((file, index) => (
                <li key={index}>{file.name}</li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
      <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1 }}>
        ※「グループ」が検索キーになります。半角英数字で入力してください。
      </Typography>

      <DialogActions>
        <Button
          onClick={() => handleDelete(fileData)}
          color="primary"
          disabled={isLoading}
        >
          削除
        </Button>
        <Button
          onClick={() => {
            if (!fileData?.filename) {
              handleUpload();
            } else {
              handleUpdate(fileData);
            }
          }}
          color="primary" disabled={isLoading}>
          保存
        </Button>
        <Button onClick={onClose} color="secondary" disabled={isLoading}>
          キャンセル
        </Button>
      </DialogActions>

      <Backdrop open={isLoading} sx={{ color: "#fff", zIndex: 2000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default FileBUUploadModal;